/**
 * Storage Abstraction Layer
 * 
 * Provides a unified storage interface that works for both web (localStorage)
 * and React Native (AsyncStorage).
 * 
 * Usage:
 *   import { storage } from '../utils/storage';
 *   
 *   // Set item
 *   await storage.setItem('key', 'value');
 *   
 *   // Get item
 *   const value = await storage.getItem('key');
 *   
 *   // Remove item
 *   await storage.removeItem('key');
 *   
 *   // Clear all
 *   await storage.clear();
 */

// Detect if we're in React Native
// Check for React Native environment without requiring the module
const isReactNative = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';

let storageImpl;

if (isReactNative) {
  // React Native: Use AsyncStorage
  // Only try to require if we're actually in React Native
  // This will fail gracefully in web builds
  try {
    // Dynamic require to avoid breaking web builds
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    storageImpl = {
      getItem: async (key) => {
        try {
          return await AsyncStorage.getItem(key);
        } catch (error) {
          console.error('Storage getItem error:', error);
          return null;
        }
      },
      setItem: async (key, value) => {
        try {
          await AsyncStorage.setItem(key, value);
        } catch (error) {
          console.error('Storage setItem error:', error);
          throw error;
        }
      },
      removeItem: async (key) => {
        try {
          await AsyncStorage.removeItem(key);
        } catch (error) {
          console.error('Storage removeItem error:', error);
          throw error;
        }
      },
      clear: async () => {
        try {
          await AsyncStorage.clear();
        } catch (error) {
          console.error('Storage clear error:', error);
          throw error;
        }
      },
      getAllKeys: async () => {
        try {
          return await AsyncStorage.getAllKeys();
        } catch (error) {
          console.error('Storage getAllKeys error:', error);
          return [];
        }
      }
    };
  } catch (error) {
    console.warn('AsyncStorage not available, falling back to localStorage');
    // Fallback to localStorage if AsyncStorage is not available
    storageImpl = null;
  }
}

// Web: Use localStorage (synchronous API, but we provide async interface for consistency)
if (!storageImpl && typeof window !== 'undefined' && window.localStorage) {
  storageImpl = {
    getItem: async (key) => {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        console.error('Storage getItem error:', error);
        return null;
      }
    },
    setItem: async (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        console.error('Storage setItem error:', error);
        throw error;
      }
    },
    removeItem: async (key) => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Storage removeItem error:', error);
        throw error;
      }
    },
    clear: async () => {
      try {
        localStorage.clear();
      } catch (error) {
        console.error('Storage clear error:', error);
        throw error;
      }
    },
    getAllKeys: async () => {
      try {
        return Object.keys(localStorage);
      } catch (error) {
        console.error('Storage getAllKeys error:', error);
        return [];
      }
    }
  };
}

// Fallback: In-memory storage (for testing or unsupported environments)
if (!storageImpl) {
  const memoryStorage = {};
  storageImpl = {
    getItem: async (key) => {
      return memoryStorage[key] || null;
    },
    setItem: async (key, value) => {
      memoryStorage[key] = value;
    },
    removeItem: async (key) => {
      delete memoryStorage[key];
    },
    clear: async () => {
      Object.keys(memoryStorage).forEach(key => delete memoryStorage[key]);
    },
    getAllKeys: async () => {
      return Object.keys(memoryStorage);
    }
  };
  console.warn('Using in-memory storage fallback. Data will be lost on page refresh.');
}

/**
 * Storage API
 * 
 * All methods return Promises for consistency across platforms.
 * On web, they resolve immediately (localStorage is synchronous).
 * On React Native, they resolve when AsyncStorage completes.
 */
export const storage = {
  /**
   * Get an item from storage
   * @param {string} key - Storage key
   * @returns {Promise<string|null>} - Stored value or null
   */
  getItem: storageImpl.getItem.bind(storageImpl),

  /**
   * Set an item in storage
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   * @returns {Promise<void>}
   */
  setItem: storageImpl.setItem.bind(storageImpl),

  /**
   * Remove an item from storage
   * @param {string} key - Storage key
   * @returns {Promise<void>}
   */
  removeItem: storageImpl.removeItem.bind(storageImpl),

  /**
   * Clear all items from storage
   * @returns {Promise<void>}
   */
  clear: storageImpl.clear.bind(storageImpl),

  /**
   * Get all storage keys
   * @returns {Promise<string[]>} - Array of keys
   */
  getAllKeys: storageImpl.getAllKeys.bind(storageImpl)
};

/**
 * Synchronous storage API (web only)
 * 
 * For backward compatibility, provides synchronous methods on web.
 * These will throw an error if called in React Native.
 * 
 * Use the async API above for cross-platform compatibility.
 */
export const storageSync = {
  getItem: (key) => {
    if (isReactNative) {
      throw new Error('Synchronous storage API not available in React Native. Use async storage API instead.');
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  },
  setItem: (key, value) => {
    if (isReactNative) {
      throw new Error('Synchronous storage API not available in React Native. Use async storage API instead.');
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, value);
    }
  },
  removeItem: (key) => {
    if (isReactNative) {
      throw new Error('Synchronous storage API not available in React Native. Use async storage API instead.');
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(key);
    }
  },
  clear: () => {
    if (isReactNative) {
      throw new Error('Synchronous storage API not available in React Native. Use async storage API instead.');
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.clear();
    }
  }
};

export default storage;

